import React, { useState, useEffect } from 'react';
import { X, MapPin, TriangleAlert as AlertTriangle, Truck, Loader as Loader2, CircleCheck as CheckCircle, User } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface SelectedStop {
  installmentId: string;
  contractId: string;
  clientName: string;
  address: string;
  phone: string;
  amount: number;
  ruta: number;
  clientDataNotes?: string;
}

interface CircuitDriver {
  id: string;
  name: string;
  email: string;
}

interface Props {
  stops: SelectedStop[];
  routeDate: string;
  onClose: () => void;
  onSuccess: () => void;
}

type StepStatus = 'idle' | 'loading' | 'done' | 'error';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

async function callCircuitProxy(action: string, circuitApiKey: string, body?: Record<string, unknown>) {
  const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/circuit-proxy/${action}`;
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY}`,
    'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
  };
  const payload = { ...body, circuitApiKey };
  const res = await fetch(apiUrl, { method: 'POST', headers, body: JSON.stringify(payload) });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || `Erro ${res.status}`);
  return data;
}

async function loadCircuitApiKey(unitId: string): Promise<string> {
  const { data } = await supabase
    .from('circuit_config')
    .select('api_key')
    .eq('unit_id', unitId)
    .maybeSingle();
  return data?.api_key || '';
}

export default function CircuitRouteModal({ stops, routeDate, onClose, onSuccess }: Props) {
  const { user } = useAuth();
  const [apiKey, setApiKey] = useState('');
  const [unitName, setUnitName] = useState('');
  const [drivers, setDrivers] = useState<CircuitDriver[]>([]);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [loadingDrivers, setLoadingDrivers] = useState(true);
  const [driverError, setDriverError] = useState('');
  const [notes, setNotes] = useState('');

  const [creating, setCreating] = useState(false);
  const [stepPlan, setStepPlan] = useState<StepStatus>('idle');
  const [stepStops, setStepStops] = useState<StepStatus>('idle');
  const [stepOptimize, setStepOptimize] = useState<StepStatus>('idle');
  const [stepDistribute, setStepDistribute] = useState<StepStatus>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [done, setDone] = useState(false);

  const stopsWithoutZip = stops.filter(s => {
    const addr = s.address || '';
    return !(/\d{5}-?\d{3}/.test(addr));
  });

  useEffect(() => {
    initAndFetchDrivers();
  }, []);

  const initAndFetchDrivers = async () => {
    if (!user?.unit_id) return;
    const [key, unitData] = await Promise.all([
      loadCircuitApiKey(user.unit_id),
      supabase.from('units').select('name').eq('id', user.unit_id).maybeSingle(),
    ]);
    setApiKey(key);
    setUnitName(unitData.data?.name || '');
    if (!key) {
      setLoadingDrivers(false);
      setDriverError('API key do Circuit nao configurada. Acesse Configuracoes do Circuit primeiro.');
      return;
    }
    fetchDrivers(key);
  };

  const fetchDrivers = async (key: string) => {
    setLoadingDrivers(true);
    setDriverError('');
    try {
      const [data, customNamesResult] = await Promise.all([
        callCircuitProxy('list-drivers', key),
        user?.unit_id
          ? supabase
              .from('circuit_driver_names')
              .select('circuit_driver_id, display_name')
              .eq('unit_id', user.unit_id)
          : Promise.resolve({ data: [] }),
      ]);
      const namesMap = new Map(
        (customNamesResult.data || []).map((r: any) => [r.circuit_driver_id, r.display_name])
      );
      const driverList: CircuitDriver[] = (data.drivers || []).map((d: any) => {
        const driverId = d.id || d.name;
        const customName = namesMap.get(driverId);
        return {
          id: driverId,
          name: customName || d.displayName || d.email || d.id,
          email: d.email || '',
        };
      });
      setDrivers(driverList);
      if (driverList.length > 0) setSelectedDriver(driverList[0].id);
    } catch (err: any) {
      setDriverError(err.message || 'Erro ao carregar motoristas do Circuit');
    } finally {
      setLoadingDrivers(false);
    }
  };

  const handleCreate = async () => {
    if (!selectedDriver) return;
    setCreating(true);
    setErrorMsg('');

    const driverObj = drivers.find(d => d.id === selectedDriver);
    const dateFormatted = new Date(routeDate + 'T00:00:00').toLocaleDateString('pt-BR');
    const rutasSet = [...new Set(stops.map(s => s.ruta))].sort();
    const rutaLabel = `Ruta${rutasSet.length > 1 ? 's' : ''} ${rutasSet.join(', ')}`;
    const title = unitName
      ? `${unitName} - ${rutaLabel} - ${dateFormatted}`
      : `Cobranças ${dateFormatted} - ${rutaLabel}`;

    try {
      setStepPlan('loading');
      const planData = await callCircuitProxy('create-plan', apiKey, {
        title,
        date: routeDate,
        driverIds: [selectedDriver],
      });
      const planId = planData.id || planData.name;
      if (!planId) throw new Error('Falha ao criar plano no Circuit');
      setStepPlan('done');

      setStepStops('loading');
      const circuitStops = stops.map(s => ({
        address: { addressLineOne: s.address },
        recipient: {
          name: s.clientName,
          phone: s.phone ? `+55${s.phone.replace(/\D/g, '')}` : undefined,
          externalId: s.installmentId,
        },
        notes: s.clientDataNotes
          ? `${s.clientDataNotes}${notes ? `\n\nObs: ${notes}` : ''}`
          : `Cobrar ${formatCurrency(s.amount)}${notes ? ` | ${notes}` : ''}`,
        activity: 'delivery',
        orderInfo: {
          sellerOrderId: s.installmentId,
        },
      }));
      const stopsResult = await callCircuitProxy('import-stops', apiKey, {
        planId,
        stops: circuitStops,
      });
      setStepStops('done');

      setStepOptimize('loading');
      await callCircuitProxy('optimize-plan', apiKey, { planId });
      setStepOptimize('done');

      setStepDistribute('loading');
      await callCircuitProxy('distribute-plan', apiKey, { planId });
      setStepDistribute('done');

      if (user?.unit_id) {
        const { data: routeRow } = await supabase
          .from('circuit_routes')
          .insert({
            unit_id: user.unit_id,
            circuit_plan_id: planId,
            title,
            route_date: routeDate,
            ruta: rutasSet.length === 1 ? rutasSet[0] : null,
            driver_name: driverObj?.name || '',
            status: 'distributed',
            stops_count: stops.length,
            created_by: user.id,
          })
          .select('id')
          .maybeSingle();

        if (routeRow) {
          const stopRows = stops.map((s, i) => {
            const importedStop = stopsResult?.stops?.[i];
            return {
              circuit_route_id: routeRow.id,
              installment_id: s.installmentId,
              contract_id: s.contractId,
              circuit_stop_id: importedStop?.id || importedStop?.name || '',
              client_name: s.clientName,
              address: s.address,
              amount: s.amount,
              status: 'pending',
            };
          });
          await supabase.from('circuit_route_stops').insert(stopRows);
        }
      }

      setDone(true);
    } catch (err: any) {
      setErrorMsg(err.message || 'Erro ao criar rota');
      if (stepPlan === 'loading') setStepPlan('error');
      if (stepStops === 'loading') setStepStops('error');
      if (stepOptimize === 'loading') setStepOptimize('error');
      if (stepDistribute === 'loading') setStepDistribute('error');
    } finally {
      setCreating(false);
    }
  };

  const StepIndicator = ({ label, status }: { label: string; status: StepStatus }) => (
    <div className="flex items-center gap-3 py-2">
      {status === 'idle' && <div className="w-5 h-5 rounded-full border-2 border-gray-300" />}
      {status === 'loading' && <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />}
      {status === 'done' && <CheckCircle className="w-5 h-5 text-green-600" />}
      {status === 'error' && <AlertTriangle className="w-5 h-5 text-red-500" />}
      <span className={`text-sm ${status === 'done' ? 'text-green-700' : status === 'error' ? 'text-red-600' : status === 'loading' ? 'text-blue-700 font-medium' : 'text-gray-500'}`}>
        {label}
      </span>
    </div>
  );

  const totalAmount = stops.reduce((sum, s) => sum + s.amount, 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <MapPin className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">Criar Rota no Circuit</h2>
              <p className="text-sm text-gray-500">
                {stops.length} parada{stops.length !== 1 ? 's' : ''} - {formatCurrency(totalAmount)}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {stopsWithoutZip.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  {stopsWithoutZip.length} cliente{stopsWithoutZip.length !== 1 ? 's' : ''} sem CEP no endereco
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  A precisao da rota pode ser afetada. Recomendamos preencher o CEP de todos os clientes.
                </p>
                <div className="mt-2 space-y-1">
                  {stopsWithoutZip.slice(0, 5).map(s => (
                    <p key={s.installmentId} className="text-xs text-amber-700">- {s.clientName}</p>
                  ))}
                  {stopsWithoutZip.length > 5 && (
                    <p className="text-xs text-amber-700">e mais {stopsWithoutZip.length - 5}...</p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Truck className="w-4 h-4 inline mr-1" />
              Motorista / Motoboy
            </label>
            {loadingDrivers ? (
              <div className="flex items-center gap-2 py-3 text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Carregando motoristas do Circuit...</span>
              </div>
            ) : driverError ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700">{driverError}</p>
                <button onClick={fetchDrivers} className="text-sm text-red-600 underline mt-1 hover:text-red-800">
                  Tentar novamente
                </button>
              </div>
            ) : drivers.length === 0 ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-sm text-gray-600">
                  Nenhum motorista cadastrado no Circuit. Cadastre motoristas no painel do Circuit primeiro.
                </p>
              </div>
            ) : (
              <select
                value={selectedDriver}
                onChange={e => setSelectedDriver(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {drivers.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notas gerais (opcional)
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Ex: Cobrar apenas dinheiro, ligar antes de ir..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={2}
            />
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Clientes selecionados ({stops.length})
            </h3>
            <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {stops.map(s => (
                <div key={s.installmentId} className="px-4 py-3 flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <User className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                      <p className="text-sm font-medium text-gray-800 truncate">{s.clientName}</p>
                      <span className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">R{s.ruta}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 truncate pl-5.5">
                      {s.address || 'Endereco nao informado'}
                    </p>
                  </div>
                  <span className="text-sm font-medium text-green-700 flex-shrink-0 ml-3">
                    {formatCurrency(s.amount)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {(creating || done) && (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Progresso</h4>
              <StepIndicator label="Criando plano de rota" status={stepPlan} />
              <StepIndicator label="Adicionando paradas" status={stepStops} />
              <StepIndicator label="Otimizando rota" status={stepOptimize} />
              <StepIndicator label="Enviando para o motorista" status={stepDistribute} />
            </div>
          )}

          {errorMsg && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-700">{errorMsg}</p>
            </div>
          )}

          {done && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-green-800">
                  Rota criada e enviada com sucesso!
                </p>
                <p className="text-xs text-green-700 mt-1">
                  {stops.length} parada{stops.length !== 1 ? 's' : ''} foram adicionadas e a rota foi otimizada e enviada para o motorista.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end gap-3 rounded-b-xl">
          {done ? (
            <button
              onClick={() => { onSuccess(); onClose(); }}
              className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
            >
              Fechar
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                disabled={creating}
                className="px-4 py-2.5 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreate}
                disabled={creating || !selectedDriver || drivers.length === 0}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Criando...
                  </>
                ) : (
                  <>
                    <MapPin className="w-4 h-4" />
                    Enviar para o Circuit
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
